/**
 * restyleGeo.js
 * ---------------------------------------------------------------------
 * Central logic for rebuilding the styled GeoJSON feature collection
 * used to colour and display areas on the map.
 *
 * It supports two main modes:
 *   1. "explore" → show survey results (categorical or Likert).
 *   2. "filter"  → highlight areas matching two target variable criteria.
 *
 * This file isolates the complexity of map styling from the main
 * React component to keep UI logic clean and testable.
 *
 * @param {Object} params
 * @param {Object} params.geoData - GeoJSON FeatureCollection
 * @param {Array} params.metricRows - Array of data rows (one per area)
 * @param {Object} params.group - Active group configuration (question/variable set)
 * @param {Object} params.props - Props from parent component
 * @param {Function} params.setStyledGeo - Callback to set state in parent
 * @param {Function} params.getGroups - Function returning all groups (for label lookup)
 * @param {Function} params.buildLabelsAndColors - Helper for generating label/color mappings
 * @param {Function} params.makeHeatScale - Function for continuous heat colour mapping
 * @param {Function} params.make2pointHeatScale - Function for green→red heat scale (used for net)
 */
import { isMultiWaveGroup } from "../schemaTypes";

export function restyleGeo({
  geoData,
  metricRows,
  group,
  props,
  OVERLAP_MODE,
  setStyledGeo,
  getGroups,
  buildLabelsAndColors,
  makeHeatScale,
  make2pointHeatScale,
  onRankedUpdate,
  waveSelect
}) {
  const {
    ActionMode,
    compareWaves,
    ActionTargetSubVariables,
    ActionTargetRanges,
    handleActionTargetMAXRangesChange,
    ActionTargetConcentration,
    preferenceFilter,
    theme_colours,
  } = props;

  // --- 1️⃣ Safety checks ---
  if (!geoData) return setStyledGeo(null);
  if (!metricRows?.length || !geoData?.features?.length) return;
  if (
    !group ||
    (!group.keys?.length && ActionMode === "explore") ||
    (!group?.TargetVariable1?.keys?.length && ActionMode === "filter")
  )
    return;

  // --- 2️⃣ Helper functions ---
  const getRowId = (propsOrRow) =>
    String(
      propsOrRow?.area_cd ??
        propsOrRow?.PCON24CD ??
        propsOrRow?.WD24CD ??
        propsOrRow?.area_id ??
        ""
    );

  const computeNet = (row, polarity) => {
    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const sumArr = (keys) => keys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);
    const posKeys = toArr(polarity?.pos);
    const negKeys = toArr(polarity?.neg);
    const hasData = [...posKeys, ...negKeys].some((k) => Number.isFinite(Number(row?.[k])));
    if (!hasData) return null;
    const pos = sumArr(posKeys);
    const neg = sumArr(negKeys);
    return pos - neg;
  };

  const computeRange = (rows, variable, subVar, polarity) => {
    if (subVar === "net") {
      const vals = rows.map((d) => computeNet(d, polarity));
      return [Math.min(...vals), Math.max(...vals)];
    }
    const vals = rows.map((d) => Number(d[subVar]));
    return [Math.min(...vals), Math.max(...vals)];
  };

  const getConcentrationSet = (byId, features, group, subVar, concentration) => {
    if (!features || !features.length) return null;
    if (!concentration || concentration === "all") return null;

    const pctMap = { top50: 50, top25: 25, top10: 10 };
    const pct = pctMap[concentration];
    if (!pct) return null;

    const toPercent = (x) => (!Number.isFinite(x) ? null : x);
    const toArray = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);
    const sumKeys = (keys, row) =>
      keys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);

    const values = features
      .map((f) => {
        const id = getRowId(f?.properties);
        const row = byId?.get(id);
        if (!row) return null;
        let val;
        if (subVar === "net") {
          const posKeys = toArray(group?.TargetVariable1?.polarity?.pos);
          const negKeys = toArray(group?.TargetVariable1?.polarity?.neg);
          const pos = sumKeys(posKeys, row);
          const neg = sumKeys(negKeys, row);
          val = Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
        } else {
          val = Number(row?.[subVar]);
        }
        const pctVal = toPercent(val);
        return Number.isFinite(pctVal) ? { id, v: pctVal } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.v - a.v);

    if (!values.length) return null;
    const keepCount = Math.max(1, Math.ceil((pct / 100) * values.length));
    const keep = new Set(values.slice(0, keepCount).map((d) => d.id));
    return keep;
  };

 const buildAllValues = (row, keys, labels) => {
  if (!row || typeof row !== "object") {
    return { _allValues: {}, _allValuesByKey: {}, _allValuesSorted: [], n_raked: null, total_sum: null, n: null, weight: null };
  }

  const allValues = {};
  const allValuesByKey = {};
  const allValuesSorted = [];
  keys.forEach((k) => {
    const realKey = Object.keys(row).find((rk) => rk.includes(k)) || k;
    const v = Number(row?.[realKey]);
    if (Number.isFinite(v)) {
      const label = labels?.[k] || k;
      allValues[label] = v;
      allValuesByKey[k] = v;
      allValuesSorted.push({ key: k, label, value: v });
    }
  });

  allValuesSorted.sort((a, b) => b.value - a.value);

  return {
    _allValues: allValues,
    _allValuesByKey: allValuesByKey,
    _allValuesSorted: allValuesSorted,
    n_raked: row?.n_raked ?? null,
    total_sum: row?.total_sum ?? null,
    n: row?.n ?? null,
    weight: row?.weight ?? null,
  };
};

  const styleFeature = (f, fillColor, topLabel, topValue, allValues) => ({
    ...f,
    properties: {
      ...f.properties,
      fillColor,
      topLabel,
      topValue,
      ...allValues,
    },
  });

  
  // --- First filter to the right wave ---
  // Guard: if metricRows or geoData not yet loaded, don't clear the map
  if (!metricRows || !geoData?.features) return;

  let filteredMetricRows
  if(isMultiWaveGroup(group)){
    filteredMetricRows = metricRows.filter(
              g => Number(g.Wave) === Number(waveSelect)
            )
  }else{
    filteredMetricRows = metricRows
  }



  // --- 3️⃣ Prepare data mappings ---
  const byId = new Map(filteredMetricRows.map((r) => [getRowId(r), r]));
  const { labels, colors } = buildLabelsAndColors(getGroups(), theme_colours);

  // =======================================================================
  // 🔥 FILTER MODE
  // =======================================================================
  if (ActionMode === "filter") {
    /**
     * Highlights areas meeting TargetVariable1 within a concentration threshold.
     */
    const concentration = ActionTargetConcentration || "all";
    const keepSet = getConcentrationSet(
      byId,
      geoData?.features || [],
      group,
      ActionTargetSubVariables?.TargetSubVariable1,
      concentration
    );

    // Aurora stores values as whole-number percentages (0–100 range).
    // The old toPct heuristic (x <= 1 → x * 100) incorrectly converts
    // small nets like +1pp to 100 because 1 <= 1. Just pass through as-is.
    const toPct = (x) => (!Number.isFinite(x) ? null : x);
    const selectedTargetSubVar = ActionTargetSubVariables?.TargetSubVariable1;
    const [selectedSubVarId, selectedSubMetricRaw] = String(selectedTargetSubVar || "").split("|");
    const metricToken = String(selectedSubMetricRaw || "trust").toLowerCase();
    const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    const metricMode = ["distrust", "discomfort", "neg", "negative"].includes(metricToken)
      ? "neg"
      : ["trust", "comfort", "pos", "positive"].includes(metricToken)
        ? "pos"
        : metricToken;
    const isLikertGridTarget = group?.TargetVariable1?.type === "likert_grid";
    const targetSubGroup = isLikertGridTarget ? getGroups()?.[selectedSubVarId] : null;
    const firstPos = toArr(targetSubGroup?.polarity?.pos)[0];
    const firstNeg = toArr(targetSubGroup?.polarity?.neg)[0];
    const posText = String(targetSubGroup?.labels?.[firstPos] || "").toLowerCase();
    const negText = String(targetSubGroup?.labels?.[firstNeg] || "").toLowerCase();
    const usesComfortLabels = `${posText} ${negText}`.includes("comfort");
    const posLabel = usesComfortLabels ? "Comfort" : "Trust";
    const negLabel = usesComfortLabels ? "Discomfort" : "Distrust";
    const sumArr = (keys, row) =>
      (keys || []).reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);
    const resolveTargetValue = (row) => {
      if (isLikertGridTarget) {
        const posKeys = targetSubGroup?.polarity?.pos;
        const negKeys = targetSubGroup?.polarity?.neg;
        const trustVal = sumArr(Array.isArray(posKeys) ? posKeys : [posKeys].filter(Boolean), row);
        const distrustVal = sumArr(Array.isArray(negKeys) ? negKeys : [negKeys].filter(Boolean), row);
        if (metricMode === "pos") return trustVal;
        if (metricMode === "neg") return distrustVal;
        if (metricMode === "net") {
          const netVal = trustVal - distrustVal;
          row.net1 = netVal;
          return netVal;
        }
        const v = Number(row?.[selectedSubMetricRaw]);
        return Number.isFinite(v) ? v : null;
      }
      if (selectedTargetSubVar === "net") {
        const netVal = computeNet(row, group?.TargetVariable1?.polarity || {});
        row.net1 = netVal;
        return netVal;
      }
      return Number(row?.[selectedTargetSubVar]);
    };
    const valuesForScale = (filteredMetricRows || []).map((row) => {
      const val1 = resolveTargetValue(row);
      const p1 = toPct(val1);
      return Number.isFinite(p1) ? p1 : NaN;
    });

    const values = geoData.features.map((f) => {
      const row = byId.get(getRowId(f.properties));
      if (!row) return NaN;

      const val1 = resolveTargetValue(row);
      const p1 = toPct(val1);

      const pass1 =
        Number.isFinite(p1) &&
        (keepSet == null || keepSet.has(getRowId(row)));

      row.ActionTargetKey = pass1 ? p1 : null;
      return Number(row.ActionTargetKey);
    });

    // For net scales, ensure the domain always spans at least ±30pp so that
    // small absolute values (e.g. +1%) don't map to 100% green just because
    // they happen to be the local maximum in a compressed range.
    const valuesForNetScale = [...valuesForScale, 30, -30];
    // Raw response keys carry their own polarity — a negative response
    // (e.g. windfall "Uncomfortable") heats red exactly like the distrust token.
    const negKeySelected = isLikertGridTarget
      && !["pos", "neg", "net"].includes(metricMode)
      && toArr(targetSubGroup?.polarity?.neg).includes(selectedSubMetricRaw);
    const heatScale =
      (selectedTargetSubVar === "net" || (isLikertGridTarget && metricMode === "net"))
        ? make2pointHeatScale("#2E7D32", "#C62828", valuesForNetScale)
        : makeHeatScale(
            isLikertGridTarget
              ? ((metricMode === "neg" || negKeySelected) ? "#C62828" : "#2E7D32")
              : (colors?.[selectedTargetSubVar] || "#2E7D32"),
            valuesForScale
          );

    const styled = {
      type: "FeatureCollection",
      features: geoData.features.map((f) => {
        const row = byId.get(getRowId(f.properties));
        if (!row)
          return styleFeature(f, "#ccc", "", 0, {
            _allValues: {},
            _allValuesSorted: [],
            n_raked: null,
            total_sum: null,
            n: null,
            weight: null,
          });

        const groupKeys = [
          ...((isLikertGridTarget ? targetSubGroup?.keys : group.TargetVariable1?.keys) || []),
          "ActionTargetKey",
          "net1",
          "Net",
        ];

        const allValues = buildAllValues(
          row,
          groupKeys,
          (isLikertGridTarget ? targetSubGroup?.labels : group.TargetVariable1?.labels) || group.labels
        );
        const v = Number(row.ActionTargetKey);
        const fillColor = heatScale(v);
        const topValue = Number.isFinite(v) ? v : 0;
        const topLabel = isLikertGridTarget
          ? (metricMode === "neg" ? negLabel : metricMode === "pos" ? posLabel : "Net")
          : (labels.net || "net");

        return styleFeature(f, fillColor, topLabel, topValue, allValues);
      }),
    };

    setStyledGeo(styled);

    // ---- Build ranking list for the side panel (simple & consistent) ----
    // ---- Build ranked list for TopAreasPanel (0..1 score) ----
    let ranked = [];
    try {
      const idToName = new Map(
        (geoData?.features || []).map(f => {
          const id = String(f.properties?.area_id ?? "");
          const name = f.properties?.WD24NM || f.properties?.PCON24NM || id;
          return [id, name];
        })
      );

      ranked = (filteredMetricRows || [])
        .map(r => {
          const id = String(r.area_cd ?? "");
          if (!id) return null;

          // Choose score based on your current mode/logic:
          let score; // normalized 0..1 for display
          if (ActionMode === "filter") {
            const v = Number(r.ActionTargetKey);            // you set this earlier
            score = Number.isFinite(v) ? v / 100 : null;
          } else {
            // EXPLORE: mirror your heat metric (net or single key)
            if (preferenceFilter === "net") {
              const g = this.getActiveGroup?.();
              const pos = g?.polarity?.pos ? Number(r[g.polarity.pos]) : null;
              const neg = g?.polarity?.neg ? Number(r[g.polarity.neg]) : null;
              const v = (pos != null && neg != null) ? (pos - neg) : null;
              score = v != null ? v / 100 : null;
            } else {
              const heatKey = (/* your computed heatKey in explore branch */ preferenceFilter) || (this.getActiveGroup?.().keys?.[0]);
              const v = Number(r[heatKey]);
              score = Number.isFinite(v) ? v / 100 : null;
            }
          }

          return score == null ? null : { id, name: idToName.get(id) || id, score };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score);

    } catch { /* noop */ }
    if (!(ActionMode === "filter" && compareWaves)) {
      onRankedUpdate?.(ranked || []);
    }
    return;
  }

  // =======================================================================
  // 🔍 EXPLORE MODE
  // =======================================================================
  if (ActionMode === "explore") {
    /**
     * Displays survey or metric data values.
     * - likert_grid groups: compare sectors by net score (categorical) or drill into one
     * - Likert groups: show heatmap (continuous scale)
     * - Categorical groups: colour by category or top-ranked category
     */

    // ---- LIKERT GRID ----
    if (group?.type === "likert_grid") {
      const subVarIds = group.subVariables || [];
      const isValidSubVar = subVarIds.includes((preferenceFilter || "").split("|")[0]);
      const isCompareMode = !preferenceFilter || preferenceFilter === "compare" || !isValidSubVar;
      const emptyFeature = (f) =>
        styleFeature(f, "#ccc", "", 0, {
          _allValues: {}, _allValuesSorted: [], n_raked: null, total_sum: null, n: null, weight: null,
        });

      if (isCompareMode) {
        // Categorical map: colour each area by the sector with the highest net trust
        const subGroups = (group.subVariables || [])
          .map((id) => ({ id, g: getGroups()[id] }))
          .filter((x) => x.g?.polarity);

        const styled = {
          type: "FeatureCollection",
          features: geoData.features.map((f) => {
            const row = byId.get(getRowId(f.properties));
            if (!row) return emptyFeature(f);

            const sectorNets = subGroups
              .map(({ id, g }) => ({
                key: id,
                label: group.subLabels?.[id] || id,
                value: computeNet(row, g.polarity),
              }))
              .filter((x) => Number.isFinite(x.value));

            sectorNets.sort((a, b) => b.value - a.value);
            const top = sectorNets[0];
            if (!top) return emptyFeature(f);

            const fillColor = group.subColors?.[top.key] || "#999";
            const allValues = {
              _allValues: {},
              _allValuesByKey: JSON.stringify(
                Object.fromEntries(sectorNets.map((s) => [s.key, s.value]))
              ),
              _allValuesSorted: JSON.stringify(sectorNets),
              n_raked: row.n_raked ?? null,
              total_sum: row.total_sum ?? null,
              n: row.n ?? null,
              weight: row.weight ?? null,
            };

            return styleFeature(f, fillColor, top.label, top.value, allValues);
          }),
        };

        setStyledGeo(styled);
        onRankedUpdate?.([]);
        return;
      }

      // Sub-variable selected: "trust_banks" or "trust_banks|ind:trust_banks:tend_trust:cat"
      const [subVarId, subKey] = preferenceFilter.split("|");
      const subGroup = getGroups()[subVarId];
      if (!subGroup) return;

      const isNet = !subKey;
      const values = geoData.features.map((f) => {
        const row = byId.get(getRowId(f.properties));
        if (!row) return NaN;
        return isNet ? computeNet(row, subGroup.polarity) : Number(row[subKey]);
      });

      const heatScale = isNet
        ? make2pointHeatScale("#2E7D32", "#C62828", values)
        : makeHeatScale(colors[subKey] || "#6e6e6e", values);

      const styled = {
        type: "FeatureCollection",
        features: geoData.features.map((f) => {
          const row = byId.get(getRowId(f.properties));
          const val = row
            ? isNet
              ? computeNet(row, subGroup.polarity)
              : Number(row[subKey])
            : NaN;
          const fillColor = Number.isFinite(val) ? heatScale(val) : "#ccc";
          const allValues = buildAllValues(row, subGroup.keys, subGroup.labels);
          const topLabel = isNet
            ? (group.subLabels?.[subVarId] || subVarId)
            : (subGroup.labels?.[subKey] || subKey);
          return styleFeature(f, fillColor, topLabel, val, allValues);
        }),
      };

      setStyledGeo(styled);
      onRankedUpdate?.([]);
      return;
    }

    const isLikert = group?.type === "likert";
    const pref = preferenceFilter?.toLowerCase();

    // ---------------- NON-LIKERT ----------------
    if (!isLikert) {
      let rankRequested = null;
      if (["1st", "first"].includes(pref)) rankRequested = 1;
      else if (["2nd", "second"].includes(pref)) rankRequested = 2;
      else if (["3rd", "third"].includes(pref)) rankRequested = 3;

      const selectedKey =
        preferenceFilter && group.keys.includes(preferenceFilter)
          ? preferenceFilter
          : null;

      // --- A. Colour by dominant category or rank
      if (!selectedKey) {
        const styled = {
          type: "FeatureCollection",
          features: geoData.features.map((f) => {
            const row = byId.get(getRowId(f.properties));
            if (!row)
              return styleFeature(f, "#ccc", "", 0, {
                _allValues: {},
                _allValuesSorted: [],
                n_raked: null,
                total_sum: null,
                n: null,
                weight: null,
              });

            const sorted = group.keys
              .map((k) => {
                const realKey =
                  Object.keys(row).find((rk) => rk.includes(k)) || k;
                return { k: realKey, v: Number(row?.[realKey]) };
              })
              .filter((x) => Number.isFinite(x.v))
              .sort((a, b) => b.v - a.v);

            const rankIndex = rankRequested ? rankRequested - 1 : 0;
            const target = sorted[rankIndex] || sorted[0];
            if (!target)
              return styleFeature(f, "#ccc", "", 0, {
                _allValues: {},
                _allValuesSorted: [],
                n_raked: null,
                total_sum: null,
                n: null,
                weight: null,
              });

            const fillColor = colors[target.k] || "#999";
            const topLabel = group.labels?.[target.k] || target.k;
            const allValues = buildAllValues(row, group.keys, group.labels);

            // ✅ Add turnout to properties (only for Vote intention group)
            if (group.label === "Vote intention" && row?.["pol:turnout:turnout"] !== undefined) {
              allValues["pol:turnout:turnout"] = Number(row["pol:turnout:turnout"]);
            }

            return styleFeature(f, fillColor, topLabel, target.v, allValues);
          }),
        };

        setStyledGeo(styled);
        return;
      }

      // --- B. Colour by selected key as heatmap
      const values = geoData.features.map((f) => {
        const row = byId.get(getRowId(f.properties));
        return row ? Number(row[selectedKey]) : NaN;
      });

      const base = colors[selectedKey] || "#6e6e6e";
      const heatScale = makeHeatScale(base, values);

      const styled = {
        type: "FeatureCollection",
        features: geoData.features.map((f) => {
          const row = byId.get(getRowId(f.properties));
          const val = row ? Number(row[selectedKey]) : NaN;
          const fillColor = Number.isFinite(val) ? heatScale(val) : "#ccc";
          const allValues = buildAllValues(row, group.keys, group.labels);
          const topLabel = group.labels?.[selectedKey] || selectedKey;
          return styleFeature(f, fillColor, topLabel, val, allValues);
        }),
      };

      setStyledGeo(styled);
      return;
    }

    // ---------------- LIKERT ----------------
    if (preferenceFilter === "top") {
      const dkKeys = (() => {
        const toArr = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);
        return new Set(toArr(group?.polarity?.dk));
      })();
      const candidateKeys = (group.keys || []).filter((k) => !dkKeys.has(k));

      const styled = {
        type: "FeatureCollection",
        features: geoData.features.map((f) => {
          const row = byId.get(getRowId(f.properties));
          if (!row) {
            return styleFeature(f, "#ccc", "", 0, {
              _allValues: {},
              _allValuesSorted: [],
              n_raked: null,
              total_sum: null,
              n: null,
              weight: null,
            });
          }

          const ranked = candidateKeys
            .map((k) => ({ k, v: Number(row?.[k]) }))
            .filter((x) => Number.isFinite(x.v))
            .sort((a, b) => b.v - a.v);

          const top = ranked[0];
          if (!top) {
            return styleFeature(f, "#ccc", "", 0, buildAllValues(row, group.keys, group.labels));
          }

          const fillColor = colors[top.k] || "#999";
          const topLabel = group.labels?.[top.k] || top.k;
          const allValues = buildAllValues(row, group.keys, group.labels);
          return styleFeature(f, fillColor, topLabel, top.v, allValues);
        }),
      };

      setStyledGeo(styled);
      return;
    }

    const heatKey =
      preferenceFilter === "net" && group.polarity
        ? "net"
        : group.keys.includes(preferenceFilter)
        ? preferenceFilter
        : group.keys[0];

    const values = geoData.features.map((f) => {
      const row = byId.get(getRowId(f.properties));
      if (!row) return NaN;
      if (heatKey === "net" && group.polarity)
        return computeNet(row, group.polarity);
      return Number(row[heatKey]);
    });

    const base = colors[heatKey] || "#6e6e6e";
    const heatScale =
      heatKey === "net"
        ? make2pointHeatScale("#2E7D32", "#C62828", values)
        : makeHeatScale(base, values);

    const styled = {
      type: "FeatureCollection",
      features: geoData.features.map((f) => {
        const row = byId.get(getRowId(f.properties));
        const val = row
          ? heatKey === "net"
            ? computeNet(row, group.polarity)
            : Number(row[heatKey])
          : NaN;
        const fillColor = Number.isFinite(val) ? heatScale(val) : "#ccc";
        const allValues = buildAllValues(row, group.keys, group.labels);
        const topLabel = group.labels?.[heatKey] || heatKey;
        return styleFeature(f, fillColor, topLabel, val, allValues);
      }),
    };

    setStyledGeo(styled);
  }
}
